/* =========================================================
   TITAN SIGNS — Shopify configurator engine
   Same pricing/preview logic as the standalone site, wired
   to Shopify's cart: quantity = total cents on a $0.01
   variant, config rides as line item properties.
   ========================================================= */
(function () {
  "use strict";

  function init() {
    const rootEl = document.getElementById("titan-configurator");
    if (!rootEl || !window.TITAN_CONFIG) return;
    const CFG = window.TITAN_CONFIG;
    const handle = rootEl.dataset.productHandle;
    const p = CFG.products[handle];
    if (!p) {
      rootEl.querySelector("#tc-options").innerHTML =
        "<p class='tc-warn'>No configurator data for product handle “" + handle + "”. Add it to titan-data.js.</p>";
      return;
    }

    const $ = (s) => rootEl.querySelector(s);
    const fmt = (n) => "$" + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const has = (o) => p.options.includes(o);

    const matKeys = Object.keys(p.pricing.rates);
    const S = {
      w: p.defaultSize.w, h: p.defaultSize.h, qty: 10,
      material: matKeys[0], sides: "single",
      grommets: false, grommetPreset: "corners", grommetSpacing: 24,
      stakes: 0, gloss: false, contour: false, drillHoles: false,
      hem: true, rope: "none", polePocket: "none", polePocketSize: "2",
      windSlits: false, roundCorners: false,
      artDataUrl: null, artName: null,
    };

    /* ---------- geometry ---------- */
    function nestFit(w, h) {
      const SW = p.pricing.sheetW, SH = p.pricing.sheetH;
      if (!SW || !w || !h) return null;
      const a = { cols: Math.floor(SW / w), rows: Math.floor(SH / h), rot: false };
      const b = { cols: Math.floor(SW / h), rows: Math.floor(SH / w), rot: true };
      a.fit = a.cols * a.rows; b.fit = b.cols * b.rows;
      return a.fit >= b.fit ? a : b;
    }
    function grommetPoints(w, h, preset, sp) {
      const L = 1, R = w - 1, T = 1, B = h - 1, pts = [];
      const add = (x1, y1, x2, y2) => {
        const len = Math.hypot(x2 - x1, y2 - y1), n = Math.max(1, Math.round(len / sp));
        for (let i = 0; i <= n; i++) pts.push([x1 + (x2 - x1) * i / n, y1 + (y2 - y1) * i / n]);
      };
      const pr = CFG.grommetPresets.find((g) => g.id === preset) || CFG.grommetPresets[0];
      if (pr.edges[0] === "corners") pts.push([L, T], [R, T], [L, B], [R, B]);
      else {
        if (pr.edges.includes("top")) add(L, T, R, T);
        if (pr.edges.includes("bottom")) add(L, B, R, B);
        if (pr.edges.includes("left")) add(L, T, L, B);
        if (pr.edges.includes("right")) add(R, T, R, B);
      }
      const seen = new Set();
      return pts.filter(([x, y]) => {
        const k = x.toFixed(1) + "," + y.toFixed(1);
        if (seen.has(k)) return false;
        seen.add(k); return true;
      });
    }

    /* ---------- pricing ---------- */
    function calc() {
      const lines = [], warnings = [];
      const rate = p.pricing.rates[S.material];
      const areaT = (S.w * S.h) / 144 * S.qty;
      let base = 0;
      if (p.pricing.type === "sqft") {
        const r = S.sides === "double" ? rate.double : rate.single;
        base = areaT * (r || 0);
        lines.push([areaT.toFixed(1) + " sqft × " + fmt(r || 0) + " (" + rate.label + " " + S.sides + ")", base]);
      } else if (p.pricing.type === "sqin") {
        base = S.w * S.h * S.qty * rate.single;
        lines.push([(S.w * S.h * S.qty).toFixed(0) + " sq in × " + fmt(rate.single), base]);
      }
      const A = CFG.addonPrices;
      let ad = 0;
      const addL = (l, a) => { lines.push([l, a]); ad += a; };
      if (S.grommets && has("grommets")) {
        const n = grommetPoints(S.w, S.h, S.grommetPreset, S.grommetSpacing).length * S.qty;
        if (p.freeFinishing) addL("Grommets × " + n + " — included", 0);
        else addL("Grommets × " + n, n * A.grommetEach);
      }
      if (S.stakes > 0 && has("stakes")) addL("Step stakes × " + S.stakes, S.stakes * A.stakeEach);
      if (S.gloss && has("gloss")) addL("Gloss laminate", areaT * A.glossPerSqft);
      if (S.contour && has("contour")) addL("Contour cutting", Math.max(5, areaT * A.contourPerSqft));
      if (S.drillHoles && has("drillHoles")) addL("Drill holes × " + 4 * S.qty, 4 * S.qty * A.drillHoleEach);
      if (S.roundCorners && has("roundCorners")) addL("Rounded corners", A.roundCornersFlat);
      if (has("hem") && S.hem) {
        const ln = (2 * (S.w + S.h) / 12) * S.qty;
        if (p.freeFinishing) addL("Welded hem — included", 0);
        else addL("Welded hem (" + ln.toFixed(0) + " lnft)", ln * A.hemPerLnft);
      }
      if (has("rope") && S.rope !== "none") {
        const e = S.rope === "topbottom" ? 2 : 1, ln = (S.w / 12) * e * S.qty;
        addL("Rope (" + ln.toFixed(0) + " lnft)", ln * A.ropePerLnft);
      }
      if (has("polePockets") && S.polePocket !== "none") {
        const v = ["left", "right", "leftright"].includes(S.polePocket);
        const e = ["topbottom", "leftright"].includes(S.polePocket) ? 2 : 1;
        const ln = ((v ? S.h : S.w) / 12) * e * S.qty;
        addL("Pole pockets " + S.polePocketSize + '" (' + ln.toFixed(0) + " lnft)", ln * A.polePocketPerLnft);
      }
      if (has("windSlits") && S.windSlits) {
        if (S.w >= 24 && S.h >= 24 && S.w <= 120 && S.h <= 120) addL("Wind slits × " + S.qty, S.qty * A.windSlitsFlat);
        else warnings.push('Wind slits need banners over 24"×24" and under 120"×120".');
      }
      if (p.maxSize && (S.w > p.maxSize.w || S.h > p.maxSize.h))
        warnings.push('Max size is ' + p.maxSize.w + '" × ' + p.maxSize.h + '".');
      const nest = p.pricing.sheetW ? nestFit(S.w, S.h) : null;
      if (nest) nest.sheets = nest.fit ? Math.ceil(S.qty / nest.fit) : 0;
      return { lines, warnings, total: base + ad, nest };
    }

    /* ---------- preview ---------- */
    function previewSVG() {
      const maxW = 520, maxH = 330;
      const sc = Math.min(maxW / S.w, maxH / S.h) || 1;
      const W = Math.max(40, S.w * sc), H = Math.max(30, S.h * sc), pad = 44;
      let inner;
      if (S.artDataUrl) {
        inner = '<image href="' + S.artDataUrl + '" x="0" y="0" width="' + W + '" height="' + H + '" preserveAspectRatio="xMidYMid slice"/>';
      } else {
        inner = '<rect width="' + W + '" height="' + H + '" fill="#eeeef1"/>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 - 6) + '" text-anchor="middle" fill="#9a9aa3" font-size="13" font-weight="700">UPLOAD YOUR ARTWORK</text>' +
          '<text x="' + W / 2 + '" y="' + (H / 2 + 14) + '" text-anchor="middle" fill="#9a9aa3" font-size="13" font-weight="700">TO PREVIEW YOUR SIGN</text>';
      }
      let gr = "";
      if (S.grommets && has("grommets")) {
        gr = grommetPoints(S.w, S.h, S.grommetPreset, S.grommetSpacing)
          .map(([x, y]) => '<circle cx="' + x * sc + '" cy="' + y * sc + '" r="6" fill="#fff" stroke="#555" stroke-width="2.5"/>')
          .join("");
      }
      const m = 0.25 * sc;
      return '<svg width="' + (W + pad * 2) + '" height="' + (H + pad * 2) + '" viewBox="0 0 ' + (W + pad * 2) + " " + (H + pad * 2) + '" style="max-width:100%">' +
        '<defs><clipPath id="tcclip"><rect width="' + W + '" height="' + H + '"/></clipPath></defs>' +
        '<g transform="translate(' + pad + "," + pad + ')">' +
        '<rect width="' + W + '" height="' + H + '" fill="#fff"/>' +
        '<g clip-path="url(#tcclip)">' + inner + "</g>" +
        '<rect x="' + m + '" y="' + m + '" width="' + (W - 2 * m) + '" height="' + (H - 2 * m) + '" fill="none" stroke="#d81b28" stroke-width="1" stroke-dasharray="5 4" opacity=".55"/>' + gr +
        '<rect width="' + W + '" height="' + H + '" fill="none" stroke="#1a1a1e" stroke-width="1.5"/>' +
        '<text x="' + W / 2 + '" y="-14" text-anchor="middle" fill="#55555c" font-size="12" font-weight="800">' + S.w + '"</text>' +
        '<text x="-18" y="' + H / 2 + '" text-anchor="middle" fill="#55555c" font-size="12" font-weight="800" transform="rotate(-90 -18 ' + H / 2 + ')">' + S.h + '"</text>' +
        "</g></svg>";
    }
    function nestHTML(nest) {
      if (!nest || !nest.fit) return "";
      return '<div class="tc-nest"><b>' + nest.fit + " signs</b> fit per " + p.pricing.sheetW + '"×' + p.pricing.sheetH +
        '" production sheet — your ' + S.qty + " sign" + (S.qty === 1 ? "" : "s") + " run on <b>" + nest.sheets +
        " sheet" + (nest.sheets === 1 ? "" : "s") + "</b>.</div>";
    }
    function renderPreview() {
      const c = calc();
      $("#tc-preview").innerHTML = previewSVG() + nestHTML(c.nest);
    }

    /* ---------- options UI ---------- */
    function seg(label, on, act, cls, dis) {
      return '<button type="button" class="' + (on ? (cls || "on") : "") + '" ' + (dis ? "disabled" : "") + " " + act + ">" + esc(label) + "</button>";
    }
    function tgl(label, key, on, hint) {
      return '<div class="tc-row"><div class="tc-label"><span>' + label + "</span>" + (hint ? '<span class="tc-hint">' + hint + "</span>" : "") + "</div>" +
        '<div class="tc-seg">' + seg("No", !on, 'data-b="' + key + '" data-v="0"') + seg("Yes", on, 'data-b="' + key + '" data-v="1"', "on-red") + "</div></div>";
    }
    function renderOptions() {
      const rate = p.pricing.rates[S.material];
      const rows = [];
      rows.push('<div class="tc-row"><div class="tc-label"><span>Size</span><span class="tc-hint">max ' + p.maxSize.w + '"×' + p.maxSize.h + '"</span></div>' +
        '<div class="tc-inputs"><input type="number" data-n="w" value="' + S.w + '" min="1"> <b>×</b> <input type="number" data-n="h" value="' + S.h + '" min="1"> <span>inches</span></div></div>');
      rows.push('<div class="tc-row"><div class="tc-label"><span>Quantity</span></div><div class="tc-inputs"><input type="number" data-n="qty" value="' + S.qty + '" min="1"></div></div>');
      if (matKeys.length > 1) {
        rows.push('<div class="tc-row"><div class="tc-label"><span>Material</span></div><div class="tc-seg">' +
          matKeys.map((k, i) => seg(p.pricing.rates[k].label, S.material === k, 'data-m="' + i + '"')).join("") + "</div></div>");
      }
      if (has("doubleSided")) {
        const ds = !!rate.double;
        rows.push('<div class="tc-row"><div class="tc-label"><span>Print Sides</span></div><div class="tc-seg">' +
          seg("Single", S.sides === "single", 'data-o="sides" data-v="single"') +
          seg("Double", S.sides === "double", 'data-o="sides" data-v="double"', "on-red", !ds) + "</div>" +
          (ds ? "" : '<div class="tc-warn">Double-sided isn\'t available on ' + esc(rate.label) + " — change the material to enable it.</div>") + "</div>");
      }
      if (has("grommets")) {
        rows.push('<div class="tc-row"><div class="tc-label"><span>Grommets</span><span class="tc-hint">' + (p.freeFinishing ? "included free" : fmt(CFG.addonPrices.grommetEach) + " each") + "</span></div>" +
          '<div class="tc-seg">' + seg("No", !S.grommets, 'data-b="grommets" data-v="0"') + seg("Yes", S.grommets, 'data-b="grommets" data-v="1"', "on-red") + "</div>" +
          (S.grommets ? '<div class="tc-selrow"><select data-s="grommetPreset">' +
            CFG.grommetPresets.map((g) => '<option value="' + g.id + '"' + (S.grommetPreset === g.id ? " selected" : "") + ">" + g.name + "</option>").join("") + "</select>" +
            (S.grommetPreset === "corners" ? "" : '<select data-s="grommetSpacing" data-num="1">' +
              CFG.grommetSpacings.map((g) => '<option value="' + g.inches + '"' + (S.grommetSpacing === g.inches ? " selected" : "") + ">" + g.name + "</option>").join("") + "</select>") +
            "</div>" : "") + "</div>");
      }
      if (has("hem")) rows.push(tgl("Welded Hem", "hem", S.hem, p.freeFinishing ? "included free" : "recommended"));
      if (has("rope")) rows.push('<div class="tc-row"><div class="tc-label"><span>Rope</span></div><select data-s="rope">' +
        [["none", "None"], ["top", "Top Only"], ["bottom", "Bottom Only"], ["topbottom", "Top & Bottom"]]
          .map(([v, l]) => '<option value="' + v + '"' + (S.rope === v ? " selected" : "") + ">" + l + "</option>").join("") + "</select></div>");
      if (has("polePockets")) rows.push('<div class="tc-row"><div class="tc-label"><span>Pole Pockets</span></div><div class="tc-selrow"><select data-s="polePocket">' +
        [["none", "None"], ["top", "Top Only"], ["bottom", "Bottom Only"], ["topbottom", "Top & Bottom"], ["left", "Left Only"], ["right", "Right Only"], ["leftright", "Left & Right"]]
          .map(([v, l]) => '<option value="' + v + '"' + (S.polePocket === v ? " selected" : "") + ">" + l + "</option>").join("") + "</select>" +
        (S.polePocket === "none" ? "" : '<select data-s="polePocketSize" style="max-width:90px">' +
          ["1", "2", "3", "4"].map((v) => '<option value="' + v + '"' + (S.polePocketSize === v ? " selected" : "") + ">" + v + '"</option>').join("") + "</select>") + "</div></div>");
      if (has("windSlits")) rows.push(tgl("Wind Slits", "windSlits", S.windSlits, 'banners 24"–120"'));
      if (has("stakes")) rows.push('<div class="tc-row"><div class="tc-label"><span>Step Stakes</span><span class="tc-hint">' + fmt(CFG.addonPrices.stakeEach) + " each</span></div>" +
        '<div class="tc-inputs"><input type="number" data-n="stakes" value="' + S.stakes + '" min="0"></div></div>');
      if (has("gloss")) rows.push(tgl("Gloss Finish", "gloss", S.gloss, "+" + fmt(CFG.addonPrices.glossPerSqft) + "/sqft"));
      if (has("contour")) rows.push(tgl("Contour Cut", "contour", S.contour, "cut to any shape"));
      if (has("drillHoles")) rows.push(tgl("Drill Holes (4)", "drillHoles", S.drillHoles, "for standoffs"));
      if (has("roundCorners")) rows.push(tgl("Rounded Corners", "roundCorners", S.roundCorners, ""));
      $("#tc-options").innerHTML = rows.join("");
      renderPrice();
    }
    function renderPrice() {
      const c = calc();
      $("#tc-price").innerHTML =
        c.lines.map(([l, v]) => '<div class="tc-line"><span>' + l + "</span><span>" + fmt(v) + "</span></div>").join("") +
        c.warnings.map((w) => '<div class="tc-warn">⚠ ' + w + "</div>").join("") +
        '<div class="tc-line tc-total"><span>Total</span><span>' + fmt(c.total) + "</span></div>" +
        '<div class="tc-time">✓ ' + p.production + " · Blind Shipped</div>";
      const btn = $("#tc-add");
      btn.disabled = c.total <= 0 || c.warnings.length > 0;
      btn.textContent = "Add to Cart — " + fmt(c.total);
      renderPreview();
    }

    /* ---------- events (delegated) ---------- */
    rootEl.addEventListener("click", (e) => {
      const b = e.target.closest("[data-b],[data-o],[data-m]");
      if (!b || b.disabled) return;
      if (b.dataset.m !== undefined) {
        S.material = matKeys[+b.dataset.m];
        const r = p.pricing.rates[S.material];
        if (S.sides === "double" && !r.double) S.sides = "single";
      } else if (b.dataset.o) S[b.dataset.o] = b.dataset.v;
      else if (b.dataset.b) S[b.dataset.b] = b.dataset.v === "1";
      renderOptions();
    });
    rootEl.addEventListener("change", (e) => {
      const s = e.target.closest("[data-s]");
      if (!s) return;
      S[s.dataset.s] = s.dataset.num ? +s.value : s.value;
      renderOptions();
    });
    rootEl.addEventListener("input", (e) => {
      const n = e.target.closest("[data-n]");
      if (!n) return;
      const k = n.dataset.n, min = k === "stakes" ? 0 : 1;
      let v = Math.max(min, parseFloat(n.value) || min);
      if ((k === "w" || k === "h") && p.maxSize) {
        const mx = k === "w" ? p.maxSize.w : p.maxSize.h;
        if (v > mx) { v = mx; n.value = v; }
      }
      S[k] = v;
      renderPrice();
    });

    /* ---------- artwork preview ---------- */
    const artInput = $("#tc-art");
    artInput.addEventListener("change", () => {
      const f = artInput.files[0];
      if (!f) return;
      S.artName = f.name;
      $("#tc-upload-label").innerHTML = '<span class="tc-ok">✓ ' + esc(f.name) + "</span> — click to replace";
      if (f.type.indexOf("image/") === 0) {
        const rd = new FileReader();
        rd.onload = () => { S.artDataUrl = rd.result; renderPreview(); };
        rd.readAsDataURL(f);
      } else { S.artDataUrl = null; renderPreview(); }
    });

    /* ---------- add to cart (penny-unit) ---------- */
    function propInputs() {
      const c = calc();
      const rate = p.pricing.rates[S.material];
      const props = {
        "Size": S.w + '" × ' + S.h + '"',
        "Signs Quantity": String(S.qty),
        "Material": rate.label,
      };
      if (has("doubleSided")) props["Print Sides"] = S.sides === "double" ? "Double-Sided" : "Single-Sided";
      if (S.grommets && has("grommets")) {
        const gp = CFG.grommetPresets.find((g) => g.id === S.grommetPreset);
        props["Grommets"] = gp.name + (S.grommetPreset === "corners" ? "" : " · every " + S.grommetSpacing + '"');
      }
      if (S.stakes > 0 && has("stakes")) props["Step Stakes"] = String(S.stakes);
      if (S.gloss && has("gloss")) props["Gloss Finish"] = "Yes";
      if (S.contour && has("contour")) props["Contour Cut"] = "Yes";
      if (S.drillHoles && has("drillHoles")) props["Drill Holes"] = "4 per sign";
      if (S.roundCorners && has("roundCorners")) props["Rounded Corners"] = "Yes";
      if (has("hem")) props["Welded Hem"] = S.hem ? "Yes" : "No";
      if (has("rope") && S.rope !== "none") props["Rope"] = S.rope;
      if (has("polePockets") && S.polePocket !== "none") props["Pole Pockets"] = S.polePocket + " · " + S.polePocketSize + '"';
      if (S.windSlits && has("windSlits")) props["Wind Slits"] = "Yes";
      props["Unit Price Note"] = "Line total = configured price; qty shown is pricing units (cents)";
      return Object.entries(props)
        .map(([k, v]) => '<input type="hidden" name="properties[' + esc(k) + ']" value="' + esc(v) + '">')
        .join("");
    }
    $("#tc-add").addEventListener("click", () => {
      const c = calc();
      if (c.total <= 0 || c.warnings.length) return;
      $("#tc-cents").value = Math.round(c.total * 100);
      $("#tc-props").innerHTML = propInputs();
      const form = $("#tc-form");
      const fd = new FormData(form);
      const btn = $("#tc-add");
      btn.disabled = true;
      btn.textContent = "Adding…";
      fetch("/cart/add", { method: "POST", body: fd })
        .then((r) => {
          if (!r.ok) throw new Error("cart add failed: " + r.status);
          window.location.href = "/cart";
        })
        .catch((err) => {
          btn.disabled = false;
          btn.textContent = "Add to Cart — " + fmt(c.total);
          alert("Couldn't add to cart — " + err.message + ". Try again or contact us.");
        });
    });

    renderOptions();
    renderPreview();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
